import { Link, useParams } from 'react-router-dom';
import { Accordion, AccordionItem, Checkbox, Progress } from '@heroui/react';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import { LessonFormat } from '@/widgets/Lesson';
import { useGetEnrollmentTermsQuery } from '@/entities/Enrollment';
import { getProgressPercent } from '@/shared/lib/utils/getProgressPercent';
import { formatTime } from '@/shared/lib/utils/formatTime';
export const EnrollmentTermsView = () => {
  const { enrollmentId, intakeTermId } = useParams();

  const payload = { enrollmentId: enrollmentId || '', intakeTermId: intakeTermId || '' };

  const { data, isLoading, error } = useGetEnrollmentTermsQuery(payload, {
    skip: !enrollmentId || !intakeTermId,
  });

  const number = data?.term?.number || 0;
  const groupNumber = data?.term?.groupNumber || 0;
  const modules = data?.term?.modules || [];

  const completedModules = modules.reduce((acc, module) => (module.isCompleted ? acc + 1 : acc), 0);

  const totalModules = modules.length || 0;

  const progressModulesLabel = `${completedModules} out of ${totalModules} ${totalModules > 1 ? 'modules' : 'module'} completed`;
  const progressBar = getProgressPercent(completedModules, totalModules);

  return (
    <ContentWrapper
      isLoading={isLoading}
      isError={!!error}
      error={error}
      isEmpty={!modules || modules.length === 0}
      emptyMessage="There are no modules."
      asSection
      className="flex flex-col gap-y-6 rounded-3xl bg-white p-6"
    >
      <div className="flex flex-col gap-y-2.5">
        <h3 className="text-2xl font-medium">Term {number}</h3>
        <Progress
          classNames={{
            indicator: 'bg-[#17C964]',
          }}
          size="md"
          value={progressBar}
          label={progressModulesLabel}
          showValueLabel
        />
      </div>
      <div className="w-full [&>div]:!px-0 [&>div]:!rounded-lg [&>div]:!overflow-hidden">
        <Accordion selectionMode="multiple">
          {modules.map((module) => (
            <AccordionItem
              key={module.id}
              textValue={module.title}
              classNames={{
                trigger:
                  'cursor-pointer bg-[#F9FAFB] py-7 px-4 flex items-center gap-3',
                indicator:
                  'order-first text-[#1E1E1E] w-5 h-5 min-w-5 min-h-5 flex shrink-0 [&_svg]:w-full [&_svg]:h-full [&_svg]:stroke-[2.5] [&_path]:stroke-[2.5]',
                titleWrapper: 'flex-1 min-w-0',
              }}
              title={
                <div className="flex justify-between items-center gap-2 w-full">
                  <p className="text-[20px] font-medium">{module.title}</p>
                  {module.lessons.length > 0 ? (
                    <p className="text-sm">
                      {module.lessons.length} sessions &bull; {formatTime(module.totalDuration)}
                    </p>
                  ) : null}
                </div>
              }
            >
              {module.lessons.length > 0 ? (
                <ul>
                  {module.lessons.map((lesson) => {
                    const baseUrl = `modules/${module.intakeModuleId}/groups/${groupNumber}`;
                    const to = !groupNumber
                      ? ''
                      : lesson.groupLessonId
                        ? `${baseUrl}/groupLesson/${lesson.groupLessonId}`
                        : lesson.groupExamResubmissionId
                          ? `${baseUrl}/groupExamResubmission/${lesson.groupExamResubmissionId}`
                          : '';
                    const key = lesson.groupLessonId
                      ? `${lesson.groupLessonId}-lesson`
                      : lesson.groupExamResubmissionId
                        ? `${lesson.groupExamResubmissionId}-resubmission`
                        : lesson.id;
                    return (
                      <li
                        key={key}
                        className="flex justify-between items-center gap-4 px-[30px] py-4"
                      >
                        <div className="flex items-center gap-x-1">
                          <Checkbox
                            isSelected={lesson.isCompleted}
                            classNames={{
                              base: 'pointer-events-none',
                              wrapper: [
                                lesson.isCompleted ? 'after:bg-green-secondary' : '',
                                lesson.isCompleted ? 'before:border-green-secondary' : '',
                              ],
                            }}
                          />
                          {to ? <Link to={to}>{lesson.title}</Link> : <p>{lesson.title}</p>}
                        </div>
                        <div className="flex items-center gap-x-4">
                          {lesson.format && <LessonFormat format={lesson.format} />}
                          <p className="min-w-[56px]">{formatTime(lesson.duration)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </ContentWrapper >
  );
};
